import * as React from "react";
import { useCurrentProject } from "@/lib/project";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, MoreHorizontal, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/bespoke";
import { formatDateTime } from "@/lib/format";

export type EvalDataset = {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
};

async function fetchDatasets(projectId: string): Promise<EvalDataset[]> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/datasets`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return [];
  return (await response.json()).datasets;
}

async function createDataset(
  projectId: string,
  input: { name: string; description?: string },
): Promise<EvalDataset> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/datasets`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function updateDataset(
  projectId: string,
  datasetId: string,
  input: { name?: string; description?: string | null },
): Promise<EvalDataset> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/datasets/${datasetId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function deleteDataset(projectId: string, datasetId: string): Promise<void> {
  await fetch(`/api/v1/projects/${projectId}/eval/datasets/${datasetId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
}

function DatasetRow({
  dataset,
  onEdit,
  onDelete,
}: {
  dataset: EvalDataset;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="group hover:bg-muted/50">
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-medium">
        {dataset.name}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <Badge variant="secondary">{dataset.itemCount} items</Badge>
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground max-w-xs truncate">
        {dataset.description ?? "—"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {formatDateTime(dataset.updatedAt)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive-text">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

export function EvalDatasetsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const queryClient = useQueryClient();

  const { data: datasets, isLoading } = useQuery({
    queryKey: ["eval-datasets", projectId],
    queryFn: () => fetchDatasets(projectId!),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; description?: string }) => createDataset(projectId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-datasets", projectId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      datasetId,
      input,
    }: {
      datasetId: string;
      input: { name?: string; description?: string | null };
    }) => updateDataset(projectId!, datasetId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-datasets", projectId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (datasetId: string) => deleteDataset(projectId!, datasetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-datasets", projectId] });
    },
  });

  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editDescription, setEditDescription] = React.useState("");
  const [search, setSearch] = React.useState("");

  const filteredDatasets =
    datasets?.filter(
      (d) =>
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.description?.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createMutation.mutate({
      name: formData.get("name") as string,
      description: (formData.get("description") as string) || undefined,
    });
    setIsCreateOpen(false);
    e.currentTarget.reset();
  };

  const handleUpdate = (datasetId: string) => {
    updateMutation.mutate({ datasetId, input: { name: editName, description: editDescription } });
    setEditingId(null);
  };

  const handleDelete = (datasetId: string) => {
    if (confirm("Are you sure you want to delete this dataset?")) {
      deleteMutation.mutate(datasetId);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Datasets"
        headingLevel={headingLevel}
        description="Collections of input/output pairs for evaluation runs."
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New dataset
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search datasets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {isCreateOpen && (
            <form
              onSubmit={handleCreate}
              className="mb-4 p-4 border rounded-lg bg-muted/30 space-y-3"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input name="name" placeholder="My eval dataset" required autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description (optional)</label>
                  <Input name="description" placeholder="What this dataset is for" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create dataset"}
                </Button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Datasets</caption>
              <thead>
                <tr className="border-b">
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Items
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Updated
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  ></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-muted-foreground">
                      Loading datasets...
                    </td>
                  </tr>
                ) : filteredDatasets.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8">
                      <p className="font-medium">No datasets yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Create a dataset to start collecting eval inputs and expected outputs.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredDatasets.map((dataset) => (
                    <DatasetRow
                      key={dataset.id}
                      dataset={dataset}
                      onEdit={() => {
                        setEditName(dataset.name);
                        setEditDescription(dataset.description ?? "");
                        setEditingId(dataset.id);
                      }}
                      onDelete={() => handleDelete(dataset.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {editingId && (
            <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description (optional)</label>
                  <Input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => handleUpdate(editingId)}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
